import { createSlice } from '@reduxjs/toolkit';

// Mock question sets for different scenarios
const MOCK_QUESTION_SETS = {
  event_preferences: {
    id: 'event_preferences',
    name: 'Event Preferences',
    description: 'Basic event interest questionnaire',
    questions: [
      {
        id: 1,
        question: "What's your name?",
        type: 'text',
        field: 'name',
        required: true,
        placeholder: 'Enter your full name'
      },
      {
        id: 2,
        question: "What's your email address?",
        type: 'email',
        field: 'email',
        required: true,
        placeholder: 'your.email@example.com'
      },
      {
        id: 3,
        question: "What type of events are you most interested in?",
        type: 'multi-select',
        field: 'primary_interests',
        options: [
          'Concerts & Music',
          'Food & Dining',
          'Sports Events',
          'Educational Workshops',
          'Wedding Expos',
          'Corporate Events',
          'Festivals & Fairs',
          'Networking Mixers'
        ],
        required: true
      },
      {
        id: 4,
        question: "What's your budget range for events?",
        type: 'single-select',
        field: 'budget_range',
        options: [
          'Under $50',
          '$50 - $100',
          '$100 - $250',
          '$250 - $500',
          '$500+'
        ],
        required: false
      },
      {
        id: 5,
        question: "How many people usually attend events with you?",
        type: 'number',
        field: 'group_size',
        required: false,
        placeholder: 'Enter number (e.g., 2)'
      },
      {
        id: 6,
        question: "Any specific preferences or requirements?",
        type: 'textarea',
        field: 'comments',
        required: false,
        placeholder: 'Tell us more about your preferences...'
      }
    ]
  },
  wedding_planning: {
    id: 'wedding_planning',
    name: 'Wedding Planning',
    description: 'Wedding event planning questionnaire',
    questions: [
      {
        id: 1,
        question: "What's your wedding date?",
        type: 'date',
        field: 'wedding_date',
        required: false
      },
      {
        id: 2,
        question: "Wedding venue type preference?",
        type: 'single-select',
        field: 'venue_type',
        options: ['Indoor', 'Outdoor', 'Beach', 'Garden', 'Ballroom'],
        required: true
      },
      {
        id: 3,
        question: "Approximate guest count?",
        type: 'number',
        field: 'guest_count',
        required: true,
        placeholder: 'Number of guests'
      },
      {
        id: 4,
        question: "Wedding theme/style?",
        type: 'text',
        field: 'wedding_theme',
        required: false,
        placeholder: 'e.g., Rustic, Modern, Vintage'
      }
    ]
  },
  corporate_events: {
    id: 'corporate_events',
    name: 'Corporate Events',
    description: 'Corporate event planning form',
    questions: [
      {
        id: 1,
        question: "Company name?",
        type: 'text',
        field: 'company_name',
        required: true
      },
      {
        id: 2,
        question: "Event type?",
        type: 'multi-select',
        field: 'event_types',
        options: ['Conference', 'Team Building', 'Product Launch', 'Annual Party', 'Networking'],
        required: true
      },
      {
        id: 3,
        question: "Number of attendees?",
        type: 'number',
        field: 'attendee_count',
        required: true
      },
      {
        id: 4,
        question: "Event budget?",
        type: 'single-select',
        field: 'budget',
        options: ['Under $5,000', '$5,000 - $10,000', '$10,000 - $25,000', '$25,000+'],
        required: true
      }
    ]
  }
};

// Load initial state from localStorage if available
const loadStateFromStorage = () => {
  try {
    const serializedState = localStorage.getItem('chatState');
    if (serializedState === null) {
      return undefined;
    }
    return JSON.parse(serializedState);
  } catch (err) {
    console.error('Failed to load state from localStorage:', err);
    return undefined;
  }
};

// Initial state with first question set loaded
const initialState = loadStateFromStorage() || {
  isOpen: false,
  messages: [
    {
      id: 1,
      text: "Hi! I'm your Event Assistant. Let me help you find perfect events!",
      sender: 'bot',
      timestamp: new Date().toISOString(),
      type: 'greeting'
    }
  ],
  isTyping: false,
  showEmailForm: true,
  collectedData: [],
  submissions: [], // Store all form submissions
  currentQuestionSet: 'event_preferences',
  currentQuestionIndex: 0,
  formData: {
    name: '',
    email: '',
    phone: '',
    primary_interests: [],
    budget_range: '',
    group_size: '',
    comments: '',
    // Additional fields will be added dynamically
  }
};

const chatSlice = createSlice({
  name: 'chat',
  initialState,
  reducers: {
    toggleChat: (state) => {
      state.isOpen = !state.isOpen;
      if (state.isOpen && !state.showEmailForm) {
        state.showEmailForm = true;
      }
    },
    
    openChat: (state) => {
      state.isOpen = true;
      state.showEmailForm = true;
    },
    
    closeChat: (state) => {
      state.isOpen = false;
    },
    
    addMessage: (state, action) => {
      state.messages.push({
        id: state.messages.length + 1,
        ...action.payload,
        timestamp: new Date().toISOString()
      });
    },
    
    updateFormData: (state, action) => {
      const { field, value, questionId } = action.payload;
      const questionSet = MOCK_QUESTION_SETS[state.currentQuestionSet];
      const currentQuestion = questionSet.questions[state.currentQuestionIndex];
      
      // Store response in collectedData
      if (currentQuestion) {
        state.collectedData.push({
          questionSetId: state.currentQuestionSet,
          questionId: questionId || currentQuestion.id,
          question: currentQuestion.question,
          field: currentQuestion.field,
          value: value,
          timestamp: new Date().toISOString()
        });
      }
      
      // Update form data based on field type
      if (currentQuestion?.type === 'multi-select') {
        if (!state.formData[field]) {
          state.formData[field] = [];
        }
        if (state.formData[field].includes(value)) {
          state.formData[field] = state.formData[field].filter(item => item !== value);
        } else {
          state.formData[field].push(value);
        }
      } else {
        state.formData[field] = value;
      }
      
      // Auto-advance for required questions
      if (currentQuestion?.required && value && value.toString().trim()) {
        if (state.currentQuestionIndex < questionSet.questions.length - 1) {
          state.currentQuestionIndex += 1;
        }
      }
    },
    
    nextQuestion: (state) => {
      const questionSet = MOCK_QUESTION_SETS[state.currentQuestionSet];
      if (state.currentQuestionIndex < questionSet.questions.length - 1) {
        state.currentQuestionIndex += 1;
      }
    },
    
    prevQuestion: (state) => {
      if (state.currentQuestionIndex > 0) {
        state.currentQuestionIndex -= 1;
      }
    },
    
    skipQuestion: (state) => {
      const questionSet = MOCK_QUESTION_SETS[state.currentQuestionSet];
      if (state.currentQuestionIndex < questionSet.questions.length - 1) {
        state.currentQuestionIndex += 1;
      }
    },
    
    changeQuestionSet: (state, action) => {
      state.currentQuestionSet = action.payload;
      state.currentQuestionIndex = 0;
      state.formData = {};
      state.collectedData = [];
      state.showEmailForm = true;
    },
    
    submitForm: (state) => {
      const questionSet = MOCK_QUESTION_SETS[state.currentQuestionSet];
      
      // Create submission object
      const submission = {
        id: `sub_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        questionSetId: state.currentQuestionSet,
        questionSetName: questionSet.name,
        formData: { ...state.formData },
        collectedData: [...state.collectedData],
        timestamp: new Date().toISOString()
      };
      
      // Store in submissions array
      state.submissions.push(submission);
      
      // Add success message
      state.messages.push({
        id: state.messages.length + 1,
        text: `✅ Thank you ${state.formData.name || 'there'}! Your ${questionSet.name} form has been submitted successfully. We'll contact you at ${state.formData.email || 'your provided email'} shortly.`,
        sender: 'bot',
        type: 'submission_success'
      });
      
      // Save to localStorage
      try {
        localStorage.setItem('chatSubmissions', JSON.stringify(state.submissions));
      } catch (err) {
        console.error('Failed to save to localStorage:', err);
      }
      
      // Reset for new form
      state.showEmailForm = false;
      state.currentQuestionIndex = 0;
      state.formData = {};
      state.collectedData = [];
    },
    
    resetForm: (state) => {
      state.currentQuestionIndex = 0;
      state.formData = {};
      state.collectedData = [];
      state.showEmailForm = true;
    },
    
    clearAllData: (state) => {
      state.submissions = [];
      state.messages = [{
        id: 1,
        text: "Hi! I'm your Event Assistant. Let me help you find perfect events!",
        sender: 'bot',
        timestamp: new Date().toISOString(),
        type: 'greeting'
      }];
      state.currentQuestionIndex = 0;
      state.formData = {};
      state.collectedData = [];
      state.showEmailForm = true;
      
      // Clear localStorage
      localStorage.removeItem('chatSubmissions');
    },
    
    loadSubmissionsFromStorage: (state) => {
      try {
        const submissions = localStorage.getItem('chatSubmissions');
        if (submissions) {
          state.submissions = JSON.parse(submissions);
        }
      } catch (err) {
        console.error('Failed to load submissions from localStorage:', err);
      }
    },
    
    // New: Get mock question sets
    getQuestionSets: () => {
      return MOCK_QUESTION_SETS;
    }
  }
});

// Helper function to save state to localStorage
const saveStateToStorage = (state) => {
  try {
    const serializedState = JSON.stringify(state);
    localStorage.setItem('chatState', serializedState);
  } catch (err) {
    console.error('Failed to save state to localStorage:', err);
  }
};

// Middleware to auto-save state
export const saveStateMiddleware = store => next => action => {
  const result = next(action);
  const state = store.getState();
  saveStateToStorage(state.chat);
  return result;
};

export const {
  toggleChat,
  openChat,
  closeChat,
  addMessage,
  updateFormData,
  nextQuestion,
  prevQuestion,
  skipQuestion,
  changeQuestionSet,
  submitForm,
  resetForm,
  clearAllData,
  loadSubmissionsFromStorage,
  getQuestionSets
} = chatSlice.actions;

export default chatSlice.reducer;

export { MOCK_QUESTION_SETS };